import { api, handleApiError } from '../client'
import { buildODataQuery, type ODataQueryParams } from '../odata'

// Enums matching backend
export type TeamSize = 'Solo' | 'Small' | 'Medium' | 'Large' | 'Enterprise' | 'VeryLargeEnterprise'
export type BetaRegistrationStatus = 'Pending' | 'VerificationSent' | 'Verified' | 'WelcomeSent' | 'Declined'

// Request DTOs
export interface SubmitBetaRegistrationRequest {
  fullName: string
  email: string
  company: string
  jobTitle: string
  industry?: string
  teamSize?: TeamSize
  officeAppPriorities?: string[]
  challenges?: string
  expectations?: string
  referralSource?: string
}

export interface DeclineBetaRegistrationRequest {
  reason?: string
}

// Response DTOs
export interface BetaRegistrationDto {
  id: string
  fullName: string
  email: string
  company: string
  jobTitle: string
  industry?: string
  teamSize?: TeamSize
  officeAppPriorities: string[]
  challenges?: string
  expectations?: string
  status: BetaRegistrationStatus
  verifiedAt?: string
  welcomeEmailSentAt?: string
  referralSource?: string
  declineReason?: string
  declinedAt?: string
  createdAt: string
  updatedAt: string
}

export interface BetaSubmitResponse {
  success: boolean
  message: string
  registrationId: string
}

export interface BetaVerificationResponse {
  success: boolean
  message: string
  alreadyVerified: boolean
}

export interface BetaStatsDto {
  totalRegistrations: number
  pendingCount: number
  verifiedCount: number
  welcomeSentCount: number
  declinedCount: number
  registrationsByTeamSize: Record<string, number>
  registrationsByIndustry: Record<string, number>
  registrationsByReferralSource: Record<string, number>
}

export interface BetaRegistrationsODataResponse {
  value: BetaRegistrationDto[]
  '@odata.count'?: number
}

/**
 * Normalizes an object's keys from PascalCase to camelCase.
 */
function normalizeKeys<T>(obj: Record<string, unknown>): T {
  const result: Record<string, unknown> = {}
  for (const key in obj) {
    const camelKey = key.charAt(0).toLowerCase() + key.slice(1)
    const value = obj[key]
    if (Array.isArray(value)) {
      result[camelKey] = value.map(item =>
        typeof item === 'object' && item !== null
          ? normalizeKeys(item as Record<string, unknown>)
          : item
      )
    } else if (typeof value === 'object' && value !== null) {
      result[camelKey] = normalizeKeys(value as Record<string, unknown>)
    } else {
      result[camelKey] = value
    }
  }
  return result as T
}

export const betaService = {
  /**
   * Submit a beta registration (public endpoint).
   */
  async submitRegistration(data: SubmitBetaRegistrationRequest): Promise<BetaSubmitResponse> {
    try {
      const response = await api.post<BetaSubmitResponse>('/v1/beta', data)
      return normalizeKeys<BetaSubmitResponse>(response.data as unknown as Record<string, unknown>)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Verify beta registration email (public endpoint).
   */
  async verifyEmail(token: string): Promise<BetaVerificationResponse> {
    try {
      const response = await api.get<BetaVerificationResponse>(`/v1/beta/verify/${token}`)
      return normalizeKeys<BetaVerificationResponse>(response.data as unknown as Record<string, unknown>)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get beta registrations with OData query support (admin only).
   */
  async getRegistrations(params?: ODataQueryParams): Promise<BetaRegistrationsODataResponse> {
    try {
      const queryString = params ? buildODataQuery(params) : ''
      const response = await api.get<BetaRegistrationsODataResponse | BetaRegistrationDto[]>(`/v1/beta${queryString}`)

      // Handle both OData response and array response for backwards compatibility
      if (Array.isArray(response.data)) {
        return {
          value: response.data.map(item => normalizeKeys<BetaRegistrationDto>(item as unknown as Record<string, unknown>)),
          '@odata.count': response.data.length
        }
      }

      // Normalize OData response - keys are PascalCase from OData
      const data = response.data as { value?: unknown[]; '@odata.count'?: number }
      return {
        value: (data.value || []).map(item => normalizeKeys<BetaRegistrationDto>(item as unknown as Record<string, unknown>)),
        '@odata.count': data['@odata.count']
      }
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get a single beta registration by ID (admin only).
   */
  async getRegistration(id: string): Promise<BetaRegistrationDto> {
    try {
      const response = await api.get<BetaRegistrationDto>(`/v1/beta/${id}`)
      return normalizeKeys<BetaRegistrationDto>(response.data as unknown as Record<string, unknown>)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Resend verification email (admin only).
   */
  async resendVerification(id: string): Promise<void> {
    try {
      await api.post(`/v1/beta/${id}/resend`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Decline a registration (admin only).
   */
  async declineRegistration(id: string, reason?: string): Promise<void> {
    try {
      await api.post(`/v1/beta/${id}/decline`, { reason })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Delete a registration (admin only).
   */
  async deleteRegistration(id: string): Promise<void> {
    try {
      await api.delete(`/v1/beta/${id}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get beta registration statistics (admin only).
   */
  async getStats(): Promise<BetaStatsDto> {
    try {
      const response = await api.get<BetaStatsDto>('/v1/beta/stats')
      return normalizeKeys<BetaStatsDto>(response.data as unknown as Record<string, unknown>)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
